import { InputData } from "../../models/summary/Config";

// Same address shape the SEND_TO_SHOP form already validates (SendCustomSummaryCard.tsx:33).
const mailformat = /^[^@]+@[^@]+\.[^@]+$/;

// Whether to email the PDF summary to the consumer after a recipient send.
//
// NGOP-1499: the DS Web Admin account setting (AppCustomization.SendSummaryCopyToUserEnabled) is the
// SINGLE control, per the PRD — if the account allows it and the consumer typed a valid address into the
// card's email field, they get a copy. Absent/null means enabled, so the setting reads as default-ON.
//
// This deliberately no longer consults the per-field `sendSummaryEmail` opt-in that NGOP-1491 added.
// Two controls for one behaviour meant no honest label could describe the DSWA checkbox, since ticking
// it did nothing unless a resource-root config file also opted in. Having one authority, in the place an
// admin can actually reach, is the point.
//
// Known consequence, accepted when this was decided: a config with an enabled email field on an enabled
// send-to-shop card but no `sendSummaryEmail` flag starts sending copies it did not send before. Surveyed
// across all 99 summary configs in resource_root at the time of the change: 9 such cards were live, 2
// already sent (Simpolo summary.json, SwiftDS_dev summary_sv.json) and 7 began — Cedam, Hoganas,
// Porcelaingres, HauffMach (x2 configs) and SwiftDS_trunk (x2). Untick the DSWA setting for any account
// that should stay silent. Note the unit is the config FILE, not the account: most language variants have
// the card disabled and were never in scope, so surveying one summary.json per account undercounts.
//
// Granularity deliberately lost here: `sendSummaryEmail` sat in the per-language summary config, so a
// brand could enable the copy for one language only (SwiftDS_dev did exactly that, Swedish only). The
// DSWA setting is one value per account, so such a brand now sends in every language whose card is
// enabled. That is inherent to having a single control, not an oversight.
//
// Whether the field EXISTS is still a card-config decision: the card filters `enabled !== false` before
// looking for the email input, so an absent or disabled field means no copy regardless of the setting.
//
// This gate is client-side on purpose, and it cannot move to the backend as things stand: the copy is a
// plain POST to send_customer_mail, which is also the endpoint behind the standalone "email the summary
// to myself" card (SendSummaryCard.tsx). Enforcing the account flag inside that action would silently
// disable that card too, for an account that only meant to stop the automatic copy. So the flag is a
// brand preference the client honours, not a control the server enforces — anyone calling
// send_customer_mail directly still gets a mail.
export function shouldSendSummaryEmail(
    emailField: InputData | undefined,
    emailValue: string | undefined,
    accountCopyEnabled?: boolean | null
): boolean {
    if (accountCopyEnabled === false) {
        return false;
    }
    return !!emailField && !!emailValue && mailformat.test(emailValue);
}
